import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useApi, discoveryApiRef, identityApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { kubernetesApiRef } from '@backstage/plugin-kubernetes';
import { oidcAuthApiRef } from '../../../apis';
import {
  TektonPipeline,
  TektonPipelineRun,
  TektonResultsResult,
  WorkflowType,
  WORKFLOW_TYPE,
  convertResultToRun,
  generatePipelineRunName,
  getPipelineRunStatus,
} from '../types';

export interface UseTektonPipelinesOptions {
  /** 组件名称 */
  componentName: string;
  /** 完整 Component Entity Ref，如 component:default/seal-ai-app-staging，用于后端权限校验 */
  componentEntityRef?: string;
  /** ArgoCD 应用名，用于 tracking-id 过滤（精确绑定 pipeline 到 component） */
  argoCDAppName?: string;
  /** 集群名称 */
  clusterName: string;
  /** 命名空间 */
  namespace: string;
  /** 工作流类型 */
  workflowType: WorkflowType;
  /** 轮询间隔（毫秒），默认 6000 */
  pollInterval?: number;
  /** 是否启用轮询，默认 true */
  enablePolling?: boolean;
}

export interface PipelineWithRuns {
  pipeline: TektonPipeline;
  lastRun?: TektonPipelineRun;
  recentRuns: TektonPipelineRun[];
}

export interface UseTektonPipelinesResult {
  /** Pipeline 列表（来自 K8s API） */
  pipelines: TektonPipeline[];
  /** PipelineRun 列表（来自 Tekton Results API） */
  pipelineRuns: TektonPipelineRun[];
  /** Pipeline 列表（带运行记录） */
  pipelinesWithRuns: PipelineWithRuns[];
  /** 是否正在加载 */
  loading: boolean;
  /** 错误信息 */
  error: string | null;
  /** 手动刷新 */
  refresh: () => Promise<void>;
  /** 正在执行的 Pipeline 名称集合 */
  executingPipelines: Set<string>;
  /** 静默期中的 Pipeline 名称集合 */
  pendingPipelines: Set<string>;
  /** 添加到静默期 */
  addPendingPipeline: (pipelineName: string) => void;
  /** 创建 PipelineRun */
  createPipelineRun: (
    pipeline: TektonPipeline,
    params: Array<{ name: string; value: string | string[] }>,
  ) => Promise<TektonPipelineRun>;
  /** 取消 PipelineRun */
  cancelPipelineRun: (runName: string) => Promise<void>;
  /** 生成 PipelineRun 名称 */
  generateRunName: (pipelineName: string) => string;
}

/**
 * 通用 Tekton Pipelines Hook
 * 封装了获取 Pipeline 列表、从 Tekton Results API 获取 PipelineRuns、轮询、创建和取消等核心逻辑
 */
export const useTektonPipelines = (
  options: UseTektonPipelinesOptions,
): UseTektonPipelinesResult => {
  const {
    componentName,
    componentEntityRef,
    argoCDAppName,
    clusterName,
    namespace,
    workflowType,
    pollInterval = 6000,
    enablePolling = true,
  } = options;

  // 推导完整的 componentEntityRef（用于后端 API 调用）
  const resolvedComponentEntityRef =
    componentEntityRef || `component:default/${componentName}`;

  const discoveryApi = useApi(discoveryApiRef);
  const kubernetesApi = useApi(kubernetesApiRef);
  const identityApi = useApi(identityApiRef);
  const fetchApi = useApi(fetchApiRef);
  const oidcAuthApi = useApi(oidcAuthApiRef);

  const [pipelines, setPipelines] = useState<TektonPipeline[]>([]);
  const [pipelineRuns, setPipelineRuns] = useState<TektonPipelineRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingPipelines, setPendingPipelines] = useState<Set<string>>(new Set());

  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  // 缓存已完成的 PipelineRun（key: resultsId, value: PipelineRun）
  const completedRunsCacheRef = useRef<Map<string, TektonPipelineRun>>(new Map());

  // 从 K8s API 获取 Pipeline 列表
  // 所有工作流类型都按 backstage.io/component 筛选（优先 annotations，其次 labels）
  // CDWorkflow: 额外要求 backstage.io/workflow-type 为 CDWorkflow
  // CustomWorkflow: 额外要求 backstage.io/workflow-type 为 CustomWorkflow（或无此标记）
  const fetchPipelines = useCallback(async (): Promise<TektonPipeline[]> => {
    if (!clusterName || !namespace) {
      return [];
    }

    // 筛选函数：严格绑定 pipeline 到 component
    // 优先级: ArgoCD tracking-id > backstage.io/component annotation（精确单值）> label
    // 没有任何标记时默认显示（兼容旧数据），但有标记时必须精确匹配
    const filterByComponent = (pipeline: TektonPipeline): boolean => {
      // 1. ArgoCD tracking-id 最可靠（由 ArgoCD 自动写入，格式: {appName}:{group}/{kind}:{ns}/{name}）
      if (argoCDAppName) {
        const trackingId =
          pipeline.metadata?.annotations?.['argocd.argoproj.io/tracking-id'];
        if (trackingId) {
          return trackingId.startsWith(`${argoCDAppName}:`);
        }
      }

      // 2. backstage.io/component annotation — 严格单值匹配（不再支持逗号分隔多值）
      const annotationValue =
        pipeline.metadata?.annotations?.['backstage.io/component'];
      if (annotationValue) {
        if (!componentName) return false;
        return annotationValue.trim() === componentName;
      }

      // 3. label 严格匹配
      const labelValue =
        pipeline.metadata?.labels?.['backstage.io/component'];
      if (labelValue) {
        return componentName ? labelValue === componentName : false;
      }

      // 没有任何 component 标记：默认显示（向后兼容，pipeline 部署在专属 ns 中）
      return true;
    };

    // 筛选函数：按 workflow-type 筛选（检查 annotations 或 labels）
    const filterByWorkflowType = (pipeline: TektonPipeline): boolean => {
      const annotationValue = pipeline.metadata?.annotations?.['backstage.io/workflow-type'];
      if (annotationValue) {
        return annotationValue === workflowType;
      }
      const labelValue = pipeline.metadata?.labels?.['backstage.io/workflow-type'];
      if (labelValue) {
        return labelValue === workflowType;
      }
      // 没有 workflow-type 标记的 Pipeline，仅在 CustomWorkflow 中显示（向后兼容）
      return workflowType === WORKFLOW_TYPE.CUSTOM;
    };

    // 组合筛选
    const filterPipelines = (pipelines: TektonPipeline[]): TektonPipeline[] => {
      return pipelines.filter(p => filterByComponent(p) && filterByWorkflowType(p));
    };

    // 优先尝试 v1，失败则回退到 v1beta1
    try {
      const response = await kubernetesApi.proxy({
        clusterName,
        path: `/apis/tekton.dev/v1/namespaces/${namespace}/pipelines`,
      });

      if (response.ok) {
        const data = await response.json();
        const allPipelines: TektonPipeline[] = data.items || [];
        return filterPipelines(allPipelines);
      }

      // v1 失败，尝试 v1beta1
      const v1beta1Response = await kubernetesApi.proxy({
        clusterName,
        path: `/apis/tekton.dev/v1beta1/namespaces/${namespace}/pipelines`,
      });

      if (v1beta1Response.ok) {
        const data = await v1beta1Response.json();
        const allPipelines: TektonPipeline[] = data.items || [];
        return filterPipelines(allPipelines);
      }

      return [];
    } catch {
      return [];
    }
  }, [kubernetesApi, clusterName, namespace, componentName, workflowType]);

  // 从 Tekton Results API 获取 PipelineRuns
  const fetchPipelineRuns = useCallback(async (): Promise<TektonPipelineRun[]> => {
    if (!componentName || !clusterName || !namespace) {
      return [];
    }

    try {
      const proxyUrl = await discoveryApi.getBaseUrl('proxy');

      // 使用 backstage.io/component 和 backstage.io/cluster annotations 查询
      const filter = `(summary.type=="tekton.dev/v1.PipelineRun" || summary.type=="tekton.dev/v1beta1.PipelineRun") && annotations["backstage.io/component"]=="${componentName}" && annotations["backstage.io/cluster"]=="${clusterName}"`;

      // 分页获取所有结果，按创建时间倒序（最新的排在最前面）
      let allResults: TektonResultsResult[] = [];
      let pageToken = '';
      const maxPages = 5; // 上限 1000 条（5 页 × 200 条/页）

      for (let page = 0; page < maxPages; page++) {
        const pageParam = pageToken ? `&page_token=${pageToken}` : '';
        const url = `${proxyUrl}/tekton-results/apis/results.tekton.dev/v1alpha2/parents/${namespace}/results?page_size=200&order_by=create_time+desc&filter=${encodeURIComponent(filter)}${pageParam}`;
        const response = await fetchApi.fetch(url);

        if (!response.ok) break;

        const data = await response.json();
        const pageResults: TektonResultsResult[] = data.results || [];
        allResults = allResults.concat(pageResults);

        // 如果没有下一页，停止翻页
        if (!data.next_page_token) break;
        pageToken = data.next_page_token;
      }

      const results = allResults;

      const runs: TektonPipelineRun[] = [];

      for (const result of results) {
        const summaryStatus = result.summary?.status;
        const isCompleted = summaryStatus === 'SUCCESS' || summaryStatus === 'FAILURE' || summaryStatus === 'CANCELLED';
        const isRunning = summaryStatus === 'RUNNING' || summaryStatus === 'PENDING' || !summaryStatus;

        // 已完成的：优先用缓存，否则用 summary 数据
        if (isCompleted) {
          const cached = completedRunsCacheRef.current.get(result.name);
          if (cached) {
            runs.push(cached);
          } else {
            const run = convertResultToRun(result, namespace);
            runs.push(run);
            // 缓存已完成的
            completedRunsCacheRef.current.set(result.name, run);
          }
          continue;
        }

        // 运行中的：请求 records 获取实时状态
        if (isRunning) {
          let foundPipelineRun = false;

          try {
            const recordsUrl = `${proxyUrl}/tekton-results/apis/results.tekton.dev/v1alpha2/parents/${result.name}/records?page_size=10`;
            const recordsResponse = await fetchApi.fetch(recordsUrl);

            if (recordsResponse.ok) {
              const recordsData = await recordsResponse.json();
              const records = recordsData.records || [];

              for (const record of records) {
                const recordType = record.data?.type || '';
                if (!recordType.includes('PipelineRun')) continue;

                const pipelineRunData = record.data?.value;
                if (!pipelineRunData) continue;

                try {
                  const decoded = atob(pipelineRunData);
                  const pipelineRun = JSON.parse(decoded) as TektonPipelineRun;
                  pipelineRun._resultsId = result.name;
                  runs.push(pipelineRun);
                  foundPipelineRun = true;
                  break;
                } catch {
                  // 解析失败，继续尝试下一个 record
                }
              }
            }
          } catch {
            // 出错时使用 summary 数据
          }

          if (!foundPipelineRun) {
            runs.push(convertResultToRun(result, namespace));
          }
        } else {
          // 其他状态：用 summary 数据
          runs.push(convertResultToRun(result, namespace));
        }
      }

      // 排序
      return runs.sort((a, b) =>
        new Date(b.metadata.creationTimestamp).getTime() -
        new Date(a.metadata.creationTimestamp).getTime()
      );
    } catch {
      return [];
    }
  }, [discoveryApi, fetchApi, componentName, clusterName, namespace]);

  // 刷新数据
  const refresh = useCallback(async () => {
    try {
      const [pipelineList, runList] = await Promise.all([
        fetchPipelines(),
        fetchPipelineRuns(),
      ]);
      setPipelines(pipelineList);
      setPipelineRuns(runList);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : '获取数据失败');
    }
  }, [fetchPipelines, fetchPipelineRuns]);

  // 初始加载和轮询
  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      setLoading(true);
      try {
        const [pipelineList, runList] = await Promise.all([
          fetchPipelines(),
          fetchPipelineRuns(),
        ]);
        if (isMounted) {
          setPipelines(pipelineList);
          setPipelineRuns(runList);
          setError(null);
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : '获取数据失败');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    load();

    // 启动轮询（只轮询 PipelineRuns，Pipeline 列表不需要频繁刷新）
    if (enablePolling && namespace) {
      pollIntervalRef.current = setInterval(async () => {
        if (!isMounted) return;
        try {
          const runs = await fetchPipelineRuns();
          if (isMounted) {
            setPipelineRuns(runs);
          }
        } catch {
          // ignore
        }
      }, pollInterval);
    }

    return () => {
      isMounted = false;
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [fetchPipelines, fetchPipelineRuns, enablePolling, pollInterval, namespace]);

  // 计算正在执行的 Pipelines
  const executingPipelines = useMemo(() => {
    const executing = new Set<string>();
    pipelineRuns.forEach(run => {
      const status = getPipelineRunStatus(run);
      if (['Running', 'Pending'].includes(status)) {
        const pipelineName = run.spec?.pipelineRef?.name;
        if (pipelineName) {
          executing.add(pipelineName);
        }
      }
    });
    // 合并静默期中的 Pipeline
    pendingPipelines.forEach(name => executing.add(name));
    return executing;
  }, [pipelineRuns, pendingPipelines]);

  // 为每个 Pipeline 关联最近的 PipelineRuns
  const pipelinesWithRuns: PipelineWithRuns[] = useMemo(() => {
    return pipelines.map(pipeline => {
      const relatedRuns = pipelineRuns
        .filter(run => run.spec?.pipelineRef?.name === pipeline.metadata.name)
        .sort((a, b) =>
          new Date(b.metadata.creationTimestamp).getTime() -
          new Date(a.metadata.creationTimestamp).getTime()
        );

      return {
        pipeline,
        lastRun: relatedRuns[0],
        recentRuns: relatedRuns.slice(0, 5),
      };
    });
  }, [pipelines, pipelineRuns]);

  // 添加到静默期
  const addPendingPipeline = useCallback((pipelineName: string) => {
    setPendingPipelines(prev => new Set(prev).add(pipelineName));

    // 3 秒后移除
    setTimeout(() => {
      setPendingPipelines(prev => {
        const next = new Set(prev);
        next.delete(pipelineName);
        return next;
      });
    }, 3000);
  }, []);

  // 生成 PipelineRun 名称
  const generateRunName = useCallback((pipelineName: string): string => {
    return generatePipelineRunName(pipelineName);
  }, []);

  // 创建 PipelineRun（调用后端 pipeline-management API，包含权限校验）
  const createPipelineRun = useCallback(async (
    pipeline: TektonPipeline,
    params: Array<{ name: string; value: string | string[] }>,
  ): Promise<TektonPipelineRun> => {
    // 获取当前用户信息（用于 annotations 记录触发人）
    const identity = await identityApi.getBackstageIdentity();
    const userRef = identity?.userEntityRef || 'unknown';

    let userEmail: string | undefined;
    try {
      const profile = await oidcAuthApi.getProfile();
      userEmail = profile?.email;
    } catch { /* ignore */ }

    const pipelineRunName = generateRunName(pipeline.metadata.name);
    const apiVersion = pipeline.apiVersion || 'tekton.dev/v1';

    const pipelineRun = {
      apiVersion,
      kind: 'PipelineRun',
      metadata: {
        name: pipelineRunName,
        namespace: pipeline.metadata.namespace,
        annotations: {
          'backstage.io/cluster': clusterName,
          'backstage.io/component': componentName,
          'backstage.io/workflow-type': workflowType,
          'backstage.io/triggered-by': userEmail || userRef,
        },
        ownerReferences: pipeline.metadata.uid
          ? [
              {
                apiVersion,
                kind: 'Pipeline',
                name: pipeline.metadata.name,
                uid: pipeline.metadata.uid,
                controller: false,
                blockOwnerDeletion: false,
              },
            ]
          : undefined,
      },
      spec: {
        pipelineRef: { name: pipeline.metadata.name },
        params,
      },
    };

    // 获取用户 Bearer token，传给后端做权限校验
    const { token } = await identityApi.getCredentials();
    const scaffolderBaseUrl = await discoveryApi.getBaseUrl('scaffolder');

    // Knative 工作流使用独立接口（不同的命名空间隔离逻辑）
    const triggerEndpoint = workflowType === WORKFLOW_TYPE.KNATIVE
      ? 'trigger-knative'
      : 'trigger-custom';

    const response = await fetchApi.fetch(
      `${scaffolderBaseUrl}/pipeline-management/${triggerEndpoint}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          componentRef: resolvedComponentEntityRef,
          clusterName,
          pipelineRun,
        }),
      },
    );

    if (!response.ok) {
      const errData = await response.json().catch(() => ({})) as { error?: string };
      throw new Error(
        errData.error || `创建 PipelineRun 失败: ${response.status}`,
      );
    }

    // 添加到静默期（避免立即轮询到旧状态）
    addPendingPipeline(pipeline.metadata.name);

    return response.json();
  }, [clusterName, componentName, workflowType, resolvedComponentEntityRef, generateRunName, addPendingPipeline, identityApi, oidcAuthApi, discoveryApi, fetchApi]);

  // 取消 PipelineRun
  const cancelPipelineRun = useCallback(async (runName: string, runNamespace?: string): Promise<void> => {
    const targetNamespace = runNamespace || namespace;
    if (!targetNamespace) {
      throw new Error('取消 PipelineRun 失败: namespace 未指定');
    }

    // 使用已获取的 Pipeline 的 apiVersion，保持一致
    const apiVersion = pipelines[0]?.apiVersion || 'tekton.dev/v1';

    // v1beta1 使用 PipelineRunCancelled，v1 使用 Cancelled
    const cancelStatus = apiVersion.includes('v1beta1') ? 'PipelineRunCancelled' : 'Cancelled';

    const response = await kubernetesApi.proxy({
      clusterName,
      path: `/apis/${apiVersion}/namespaces/${targetNamespace}/pipelineruns/${runName}`,
      init: {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/merge-patch+json' },
        body: JSON.stringify({ spec: { status: cancelStatus } }),
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`取消 PipelineRun 失败: ${response.status} ${errorText}`);
    }
  }, [kubernetesApi, clusterName, namespace, pipelines]);

  return {
    pipelines,
    pipelineRuns,
    pipelinesWithRuns,
    loading,
    error,
    refresh,
    executingPipelines,
    pendingPipelines,
    addPendingPipeline,
    createPipelineRun,
    cancelPipelineRun,
    generateRunName,
  };
};

export default useTektonPipelines;
